import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { 
  Settings, 
  Zap, 
  Image, 
  MessageSquare, 
  Check, 
  X, 
  Loader2,
  Eye,
  EyeOff,
  RefreshCw,
  DollarSign
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LLMConfiguration = ({ auth }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  const [config, setConfig] = useState({
    provider: 'lemonfox',
    emergent_key: '',
    lemonfox_key: '',
    default_chat_model: 'llama-8b-chat',
    default_image_model: 'sdxl'
  });
  
  const [providers, setProviders] = useState([]);
  const [models, setModels] = useState({});
  const [showEmergentKey, setShowEmergentKey] = useState(false);
  const [showLemonfoxKey, setShowLemonfoxKey] = useState(false);

  useEffect(() => {
    loadConfig();
    loadProviders();
    loadModels();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/llm/config`, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      setConfig({
        provider: response.data.provider,
        emergent_key: '',
        lemonfox_key: '',
        default_chat_model: response.data.default_chat_model,
        default_image_model: response.data.default_image_model,
        emergent_key_preview: response.data.emergent_key_preview,
        lemonfox_key_preview: response.data.lemonfox_key_preview,
        emergent_key_set: response.data.emergent_key_set,
        lemonfox_key_set: response.data.lemonfox_key_set
      });
    } catch (error) {
      console.error('Error loading config:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProviders = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/llm/providers`);
      setProviders(response.data.providers);
    } catch (error) {
      console.error('Error loading providers:', error);
    }
  };

  const loadModels = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/llm/models`, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      setModels(response.data);
    } catch (error) {
      console.error('Error loading models:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {
        provider: config.provider,
        default_chat_model: config.default_chat_model,
        default_image_model: config.default_image_model
      };
      
      if (config.emergent_key) {
        updateData.emergent_key = config.emergent_key;
      }
      if (config.lemonfox_key) {
        updateData.lemonfox_key = config.lemonfox_key;
      }
      
      await axios.put(`${BACKEND_URL}/api/llm/config`, updateData, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      
      toast({
        title: "Configuration Saved",
        description: "LLM configuration has been updated successfully."
      });
      
      loadConfig();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save configuration",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async (provider = null) => {
    setTesting(true);
    setTestResult(null);
    try {
      const url = provider 
        ? `${BACKEND_URL}/api/llm/test?provider=${provider}`
        : `${BACKEND_URL}/api/llm/test`;
        
      const response = await axios.post(url, {}, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      
      setTestResult(response.data);
      
      if (response.data.success) {
        toast({
          title: "Connection Successful",
          description: `Connected to ${response.data.provider}. Response: "${response.data.message}"`
        });
      } else {
        toast({
          title: "Connection Failed",
          description: response.data.error,
          variant: "destructive"
        });
      }
    } catch (error) {
      setTestResult({ success: false, error: error.message });
      toast({
        title: "Test Failed",
        description: error.response?.data?.detail || error.message,
        variant: "destructive"
      });
    } finally {
      setTesting(false);
    }
  };

  const currentProvider = providers.find(p => p.id === config.provider);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold flex items-center gap-2">
            <Settings className="h-5 w-5 md:h-6 md:w-6" />
            LLM Configuration
          </h2>
          <p className="text-xs md:text-sm text-slate-600 mt-1">
            Configure AI providers for chat and image generation
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="w-full sm:w-auto">
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Configuration'
          )}
        </Button>
      </div>

      {/* Provider Selection */}
      <Card>
        <CardHeader className="p-4 md:p-6">
          <CardTitle className="flex items-center gap-2 text-base md:text-lg">
            <Zap className="h-4 w-4 md:h-5 md:w-5" />
            Active Provider
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0">
          <div className="grid gap-3 md:gap-4 grid-cols-1 md:grid-cols-2">
            {providers.map((provider) => (
              <div
                key={provider.id}
                onClick={() => setConfig({ ...config, provider: provider.id })}
                className={`p-3 md:p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  config.provider === provider.id
                    ? 'border-amber-500 bg-amber-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-sm md:text-lg">{provider.name}</h3>
                    <p className="text-xs md:text-sm text-slate-600 mt-1">{provider.description}</p>
                  </div>
                  {config.provider === provider.id && (
                    <Badge className="bg-amber-500 text-xs flex-shrink-0">Active</Badge>
                  )}
                </div>
                <div className="mt-2 md:mt-3 flex flex-wrap gap-1 md:gap-2">
                  {provider.features.map((feature, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs">
                      {feature}
                    </Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* API Keys */}
      <Card>
        <CardHeader className="p-4 md:p-6">
          <CardTitle className="text-base md:text-lg">API Keys</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 md:space-y-6 p-4 md:p-6 pt-0">
          {/* LemonFox Key */}
          <div>
            <label className="block text-xs md:text-sm font-medium mb-2">
              LemonFox AI API Key
              {config.lemonfox_key_set && (
                <Badge variant="outline" className="ml-2 text-green-600 border-green-300 text-xs">
                  <Check className="h-3 w-3 mr-1" /> Configured
                </Badge>
              )}
            </label>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Input
                  type={showLemonfoxKey ? 'text' : 'password'}
                  placeholder={config.lemonfox_key_preview || "Enter LemonFox API key"}
                  value={config.lemonfox_key}
                  onChange={(e) => setConfig({ ...config, lemonfox_key: e.target.value })}
                  className="text-sm pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowLemonfoxKey(!showLemonfoxKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showLemonfoxKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <Button
                variant="outline"
                onClick={() => handleTestConnection('lemonfox')}
                disabled={testing}
                className="w-full sm:w-auto"
              >
                {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Test'}
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Get your key from <a href="https://lemonfox.ai" target="_blank" rel="noopener noreferrer" className="text-amber-600 hover:underline">lemonfox.ai</a>
            </p>
          </div>

          {/* Emergent Key */}
          <div>
            <label className="block text-xs md:text-sm font-medium mb-2">
              Emergent LLM Key (Universal Key)
              {config.emergent_key_set && (
                <Badge variant="outline" className="ml-2 text-green-600 border-green-300 text-xs">
                  <Check className="h-3 w-3 mr-1" /> Configured
                </Badge>
              )}
            </label>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Input
                  type={showEmergentKey ? 'text' : 'password'}
                  placeholder={config.emergent_key_preview || "Enter Emergent Universal Key"}
                  value={config.emergent_key}
                  onChange={(e) => setConfig({ ...config, emergent_key: e.target.value })}
                  className="text-sm pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowEmergentKey(!showEmergentKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showEmergentKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <Button
                variant="outline"
                onClick={() => handleTestConnection('emergent')}
                disabled={testing || !config.emergent_key_set}
                className="w-full sm:w-auto"
              >
                {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Test'}
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Supports OpenAI, Anthropic, and Google models
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Model Selection */}
      <div className="grid gap-4 md:gap-6 grid-cols-1 md:grid-cols-2">
        {/* Chat Model */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Chat Model
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {models[config.provider]?.chat?.map((model) => (
                <div
                  key={model.id}
                  onClick={() => setConfig({ ...config, default_chat_model: model.id })}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    config.default_chat_model === model.id
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{model.name}</p>
                      <p className="text-xs text-slate-500">{model.description}</p>
                    </div>
                    {model.price && (
                      <Badge variant="outline" className="text-xs">
                        {model.price}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Image Model */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Image className="h-5 w-5" />
              Image Model
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {models[config.provider]?.image?.map((model) => (
                <div
                  key={model.id}
                  onClick={() => setConfig({ ...config, default_image_model: model.id })}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    config.default_image_model === model.id
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{model.name}</p>
                      <p className="text-xs text-slate-500">{model.description}</p>
                    </div>
                    {model.price && (
                      <Badge variant="outline" className="text-xs">
                        {model.price}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Test Connection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Test Connection
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Button
              onClick={() => handleTestConnection()}
              disabled={testing}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {testing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4 mr-2" />
                  Test Active Provider ({config.provider})
                </>
              )}
            </Button>
            
            {testResult && (
              <div className={`flex items-center gap-2 p-3 rounded-lg ${
                testResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
              }`}>
                {testResult.success ? (
                  <>
                    <Check className="h-5 w-5" />
                    <span>Connected! Model: {testResult.model}</span>
                  </>
                ) : (
                  <>
                    <X className="h-5 w-5" />
                    <span>Failed: {testResult.error}</span>
                  </>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Current Provider Info */}
      {currentProvider && (
        <Card className="bg-slate-50">
          <CardContent className="p-6">
            <h3 className="font-semibold mb-2">Currently using: {currentProvider.name}</h3>
            <p className="text-sm text-slate-600 mb-3">{currentProvider.description}</p>
            <div className="flex flex-wrap gap-2">
              {currentProvider.features.map((feature, idx) => (
                <Badge key={idx} className="bg-amber-100 text-amber-800">
                  {feature}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LLMConfiguration;
